import { getAuthCookie } from "./authCookies";

const ZERO_OID = "000000000000000000000000";

export type PaymentPaidByPayload = {
  name: string;
  global_user_id?: string;
  membership_id?: string;
  organization_id?: string;
};

export type PaymentPaidBySource = {
  name?: string | null;
  global_user_id?: string | null;
  membership_id?: string | null;
  organization_id?: string | null;
  /** @deprecated legacy single id — used only as membership fallback */
  id?: string | null;
};

function cleanId(value: unknown): string {
  const s = String(value ?? "").trim();
  if (!s || s === ZERO_OID) return "";
  return s;
}

function cleanName(value: unknown, fallback = "Unknown User"): string {
  const s = String(value ?? "").trim();
  return s || fallback;
}

/** Build paid_by from auth cookies (logged-in payment flows). */
export function buildLoggedInPaymentPaidBy(
  overrides: PaymentPaidBySource = {}
): PaymentPaidByPayload {
  const membership_id =
    cleanId(overrides.membership_id) ||
    cleanId(overrides.id) ||
    cleanId(getAuthCookie("membershipId"));
  const global_user_id =
    cleanId(overrides.global_user_id) || cleanId(getAuthCookie("globalUserId"));
  const organization_id =
    cleanId(overrides.organization_id) || cleanId(getAuthCookie("organizationId"));
  const name = cleanName(overrides.name ?? getAuthCookie("userName"));

  return {
    name,
    ...(global_user_id ? { global_user_id } : {}),
    ...(membership_id ? { membership_id } : {}),
    ...(organization_id ? { organization_id } : {}),
  };
}

/**
 * Build paid_by for public (no-login) payment routes from end-user / order party ids.
 */
export function buildEndUserPaymentPaidBy(
  source: PaymentPaidBySource = {}
): PaymentPaidByPayload {
  const membership_id = cleanId(source.membership_id) || cleanId(source.id);
  const global_user_id = cleanId(source.global_user_id);
  const organization_id = cleanId(source.organization_id);
  return {
    name: cleanName(source.name, "Customer"),
    ...(global_user_id ? { global_user_id } : {}),
    ...(membership_id ? { membership_id } : {}),
    ...(organization_id ? { organization_id } : {}),
  };
}

/** Prefer buyer/customer foundation ids from a saved sales / job order. */
export function paymentPaidByFromOrder(
  order: Record<string, unknown> | null | undefined,
  fallbackName = ""
): PaymentPaidByPayload {
  const o = order || {};
  const first = String(o.customer_first_name ?? "").trim();
  const last = String(o.customer_last_name ?? "").trim();
  const name =
    cleanName(
      [first, last].filter(Boolean).join(" ") ||
        o.agent_name ||
        o.team_member_name ||
        o.user_name ||
        fallbackName,
      "Customer"
    );

  return buildEndUserPaymentPaidBy({
    name,
    membership_id:
      o.buyer_membership_id ??
      o.customer_membership_id ??
      o.agent_membership_id ??
      o.team_member_membership_id ??
      o.agent_id ??
      o.team_member_id ??
      o.customer_id,
    global_user_id:
      o.buyer_global_user_id ??
      o.customer_global_user_id ??
      o.agent_global_user_id ??
      o.team_member_global_user_id,
    organization_id:
      o.buyer_org_id ??
      o.customer_org_id ??
      o.agent_org_id ??
      o.team_member_org_id ??
      o.brokerage_firm_org_id,
  });
}
